'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { PrintPreviewOverlay } from '@/components/common/print-preview-overlay';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { ExpandableCard } from '@/components/ui/expandable-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import type { DealerDto, IssuedTaxInvoiceRow, NtsRow, VerifyResult } from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';
import {
  buildUploadWorkbook,
  parseNtsListFile,
  parseUploadTemplateFile,
  saveWorkbook,
  type NtsListParseResult,
} from './hometax-excel';
import { DateRangeInputs } from '@/components/features/sales/invoice/date-range-inputs';
import { checkedRows, GRID_BASE } from '@/components/features/sales/invoice/grid-shared';
import { currentMonthRange } from '@/components/features/sales/invoice/issue-period';
import { fetchDealers } from '@/components/features/sales/invoice/invoice-data-source';
import { buildTaxInvoiceDocument } from './tax-invoice-build-doc';
import {
  createOrphans,
  deleteTaxInvoice,
  fetchIssuedTaxInvoices,
  fetchTaxInvoiceDocument,
  fetchUploadRows,
  reportUploadGenerated,
  verifyTaxInvoices,
} from './tax-invoice-data-source';
import { TaxInvoiceDocument } from './tax-invoice-document';
import type { TaxInvoiceDocumentDto } from './tax-invoice-document-types';

/** PRD §5.10 국세청 세금계산서 발급 접속 URL */
const HOMETAX_ISSUE_URL =
  'https://hometax.go.kr/websquare/websquare.html?w2xPath=/ui/pp/index_pp.xml&tmIdx=46&tm2lIdx=4601010000&tm3lIdx=4601010500';

/**
 * 검증상태 5종. mismatch·canceled·unassigned는 저장 컬럼이 없어 발행내역 재조회 시 사라지므로
 * verify 응답을 화면 세션 동안 들고 배지를 덧입힌다.
 *
 * unassigned(거래처미지정)는 판정을 아예 하지 않은 상태다 — 거래처가 없으면 사업자등록번호로
 * 국세청 행을 찾을 수 없어 대사가 성립하지 않는다. 서버가 저장된 승인번호를 건드리지 않는다.
 */
const VERIFY_STATUS_LABEL: Record<string, string> = {
  matched: '대사완료',
  mismatch: '금액불일치',
  canceled: '국세청취소',
  unmatched: '미대사',
  unassigned: '거래처미지정',
};

/** Radix Select 는 빈 문자열 값을 허용하지 않아 '전체'를 이 값으로 나타낸다 */
const ALL = '__all__';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 우측 탭 버튼이 여는 파일 선택기(정본 file-upload-picker / file-nts-picker). 버튼 클릭 안에서 호출해야 열린다 */
function pickFile(): Promise<File | null> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

const VERIFY_WARNING =
  '대사를 실행하면 새 판정이 대사완료가 아닌 문서의 저장된 승인번호·전송일자가 지워집니다.\n' +
  '국세청에서 내려받은 올바른 파일인지 확인했습니까?';

export function TaxInvoice() {
  const { toast } = useToast();

  const initialRange = useMemo(currentMonthRange, []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [dealCode, setDealCode] = useState('');
  const [custList, setCustList] = useState<DealerDto[]>([]);

  const [rows, setRows] = useState<IssuedTaxInvoiceRow[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  // verify 응답은 저장되지 않는 판정을 담고 있어 화면 세션 동안만 유지한다
  const [verifyResults, setVerifyResults] = useState<Map<number, VerifyResult>>(new Map());
  const [orphanCandidates, setOrphanCandidates] = useState<NtsRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<TaxInvoiceDocumentDto | null>(null);
  // 우측 "국세청 자료" 패널 - 방금 생성했거나 되읽은 업로드 양식, 불러온 매출전자세금계산서목록(원본 그대로)
  const [uploadPreview, setUploadPreview] = useState<{
    columns: string[];
    rows: unknown[][];
  } | null>(null);
  const [ntsFile, setNtsFile] = useState<NtsListParseResult | null>(null);
  const [dataTab, setDataTab] = useState<'upload' | 'nts'>('upload');


  // 필터 변경에 반응해야 안내가 즉시 사라진다. getState() 로 읽으면 다음 액션까지 남아 있다.
  const selectedStore = useFilterStore(state => state.store[0] ?? '');
  const storeNotice = selectedStore
    ? ''
    : '상단 필터에서 주유소를 선택하면 조회할 수 있습니다.';

  useEffect(() => {
    if (!selectedStore) {
      setCustList([]);
      return;
    }
    fetchDealers(selectedStore).then(setCustList).catch(() => setCustList([]));
  }, [selectedStore]);

  const storeCd = useCallback(() => {
    // 발행 API 는 발행부서 1곳만 받는다. 임의로 한 곳을 골라 조회하면 사용자가 다른 주유소를
    // 보고 있다고 착각하므로, 선택하기 전에는 조회하지 않는다(상단 필터도 단일 선택으로 제한).
    return useFilterStore.getState().store[0] ?? '';
  }, []);

  /**
   * 주유소가 선택돼 있으면 그 코드를, 아니면 null 을 준다.
   *
   * 조회뿐 아니라 생성·취소·대사 같은 파괴적 동작도 주유소 없이 나가면 안 된다.
   * 빈 storeCd 로 서버에 도달하는 경로를 만들지 않기 위해 모든 핸들러가 이걸 쓴다.
   */
  const requireStore = useCallback(() => {
    const store = storeCd();
    if (!store) {
      toast({ title: '주유소를 선택하세요', variant: 'destructive' });
      return null;
    }
    if (!DATE_RE.test(startDate) || !DATE_RE.test(endDate)) {
      toast({ title: '발행기간을 입력하세요', variant: 'destructive' });
      return null;
    }
    return store;
  }, [endDate, startDate, storeCd, toast]);

  const fail = useCallback(
    (title: string, error: unknown) => {
      toast({
        title,
        description: error instanceof Error ? error.message : '요청을 처리하지 못했습니다',
        variant: 'destructive',
      });
    },
    [toast]
  );

  const handleExpandChange = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  const handleSearch = useCallback(async () => {
    const store = requireStore();
    if (!store) return;
    setIsLoading(true);
    try {
      const result = await fetchIssuedTaxInvoices({
        storeCd: store,
        startDate,
        endDate,
        dealCode: dealCode || undefined,
      });
      setRows(result);
      setSelected([]);
    } catch (error) {
      fail('조회 실패', error);
    } finally {
      setIsLoading(false);
    }
  }, [dealCode, endDate, fail, startDate, storeCd, toast]);

  // C420 PRD §3 - 검색조건(주유소·발행기간·거래처) 변경 시 즉시 재조회(2026-08-20 질의 2 결정).
  // 조건이 갖춰진 때만 조용히 조회한다 - 토스트 안내는 수동 [조회] 버튼 경로에만 남긴다.
  useEffect(() => {
    if (!selectedStore || !DATE_RE.test(startDate) || !DATE_RE.test(endDate)) return;
    const timer = setTimeout(() => void handleSearch(), 300);
    return () => clearTimeout(timer);
  }, [selectedStore, startDate, endDate, dealCode, handleSearch]);

  /** 업로드 양식 저장 → 저장에 성공한 뒤에만 생성횟수를 올린다 */
  const handleGenerateUpload = useCallback(async () => {
    if (selected.length === 0) {
      toast({ title: '업로드할 세금계산서를 선택하세요', variant: 'destructive' });
      return;
    }
    const store = requireStore();
    if (!store) return;
    // 이미 업로드자료를 만든 적 있는 건이 섞여 있으면 확인을 받는다(C420 PRD §6 ③)
    const regenerating = rows.filter(
      row => selected.includes(row.taxNo ?? -1) && (row.hometaxUploadGeCount ?? 0) >= 1
    ).length;
    if (
      regenerating > 0 &&
      !window.confirm(
        `이미 업로드자료가 생성된 세금계산서 ${regenerating}건이 포함돼 있습니다.\n기존 자료를 포함하여 계속 진행하시겠습니까?`
      )
    ) {
      return;
    }
    try {
      const uploadRows = await fetchUploadRows(store, selected);
      const columns = uploadRows.columns ?? [];
      const dataRows = uploadRows.rows ?? [];
      if (dataRows.length === 0) {
        toast({ title: '업로드 대상 자료가 없습니다' });
        return;
      }

      const excludedApproved = uploadRows.excludedApprovedTaxNos ?? [];
      const excludedByCap = uploadRows.excludedByCapCount ?? 0;
      if (excludedApproved.length > 0 || excludedByCap > 0) {
        toast({
          title: '일부 문서가 제외됐습니다',
          description:
            `승인 완료 ${excludedApproved.length}건 제외` +
            (excludedByCap > 0 ? ` · 100건 초과로 ${excludedByCap}건 제외` : ''),
        });
      }

      setUploadPreview({ columns, rows: dataRows });
      setDataTab('upload');

      const blob = buildUploadWorkbook(columns, dataRows);
      const saved = await saveWorkbook(
        blob,
        `전자세금계산서_업로드양식_${startDate}_${endDate}.xlsx`
      );
      if (!saved) {
        toast({ title: '파일을 저장하지 않아 생성횟수를 반영하지 않았습니다' });
        return;
      }

      // 파일에 실제로 담긴 문서만 생성횟수를 올린다 - 서버와 같은 순서(작성일자, taxNo)로 잘라낸다
      const includedTaxNos = rows
        .filter(
          row =>
            row.taxNo !== undefined &&
            selected.includes(row.taxNo) &&
            !excludedApproved.includes(row.taxNo)
        )
        .sort(
          (a, b) =>
            (a.issueDate ?? '').localeCompare(b.issueDate ?? '') ||
            (a.taxNo as number) - (b.taxNo as number)
        )
        .slice(0, dataRows.length)
        .map(row => row.taxNo as number);

      const result = await reportUploadGenerated({
        storeCd: store,
        taxNos: includedTaxNos,
      });
      toast({
        title: '업로드 양식 생성 완료',
        description: `${result?.updatedCount ?? 0}건 생성횟수 반영`,
      });
      await handleSearch();
    } catch (error) {
      fail('업로드 양식 생성 실패', error);
    }
  }, [endDate, fail, handleSearch, rows, selected, startDate, storeCd, toast]);

  /** 우측 탭 "매출전자세금계산서목록" - 파일을 골라 불러오기만 한다. 대사는 [검증(매칭)]에서 */
  const handleNtsFilePicked = useCallback(
    async (file: File) => {
      try {
        const parsed = await parseNtsListFile(file);
        setNtsFile(parsed);
        setDataTab('nts');
        // PRD §9 - 집계행을 제외했으면 파일 로드 직후 알린다. 거래처명이 '○○종합계좌'처럼
        // 합계성 키워드를 품고 있으면 문서 행이 오탐으로 빠질 수 있어, 사용자가 알아챌 유일한 장치다.
        if (parsed.excludedCount > 0) {
          toast({
            title: '집계행 제외',
            description:
              `합계·소계 등으로 판정한 ${parsed.excludedCount}행을 제외했습니다. ` +
              `문서 ${parsed.rows.length}행을 불러왔습니다.`,
          });
        }
      } catch (error) {
        fail('국세청 자료 불러오기 실패', error);
      }
    },
    [fail, toast]
  );

  /** 우측 탭 "업로드 생성자료" - 저장해 둔 세금계산서등록양식 파일을 되읽어 표시한다(PRD §5) */
  const handleUploadFilePicked = useCallback(
    async (file: File) => {
      try {
        setUploadPreview(await parseUploadTemplateFile(file));
        setDataTab('upload');
      } catch (error) {
        fail('업로드 생성자료 불러오기 실패', error);
      }
    },
    [fail]
  );

  /** 검증(매칭) - 발행내역 체크 + 국세청 목록 로드가 모두 있어야 활성(PRD §7) */
  const handleVerify = useCallback(async () => {
    if (selected.length === 0 || !ntsFile) return;
    // 재실행마다 파괴적이므로 확인을 받는다
    if (!window.confirm(VERIFY_WARNING)) return;
    const store = requireStore();
    if (!store) return;
    try {
        const response = await verifyTaxInvoices({
          storeCd: store,
          startDate,
          endDate,
          dealCode: dealCode || undefined,
          taxNos: selected,
          ntsRows: ntsFile.rows,
        });

        setVerifyResults(
          new Map(
            (response.results ?? [])
              .filter(result => result.taxNo !== undefined)
              .map(result => [result.taxNo as number, result])
          )
        );
        setOrphanCandidates(response.orphanCandidates ?? []);

        const summary = response.summary;
        const unresolved = (response.unresolvedDealerRows ?? []).length;
        toast({
          title: '대사 완료',
          description:
            `대사완료 ${summary?.matched ?? 0} · 금액불일치 ${summary?.mismatch ?? 0} · ` +
            `국세청취소 ${summary?.canceled ?? 0} · 미대사 ${summary?.unmatched ?? 0}` +
            ((summary?.unassigned ?? 0) > 0
              ? ` · 거래처미지정 ${summary?.unassigned}건(판정 제외)`
              : '') +
            (unresolved > 0 ? ` · 거래처 미해석 ${unresolved}행` : ''),
        });
        await handleSearch();
    } catch (error) {
      fail('대사 실패', error);
    }
  }, [dealCode, endDate, fail, handleSearch, ntsFile, selected, startDate, storeCd, toast]);

  const handleCreateOrphans = useCallback(async () => {
    const store = requireStore();
    if (!store) return;
    if (
      !window.confirm(
        `국세청에만 있는 문서 ${orphanCandidates.length}건을 미등록문서로 등록합니다.\n` +
          '등록 즉시 생성횟수가 1이 되어 생성취소를 할 수 없습니다.'
      )
    ) {
      return;
    }
    try {
      const result = await createOrphans({ storeCd: store, rows: orphanCandidates });
      toast({
        title: '미등록문서 등록 완료',
        description:
          `${result?.createdCount ?? 0}건 등록` +
          ((result?.skippedApprNos ?? []).length > 0
            ? ` · 중복 ${(result?.skippedApprNos ?? []).length}건 건너뜀`
            : ''),
      });
      setOrphanCandidates([]);
      await handleSearch();
    } catch (error) {
      fail('미등록문서 등록 실패', error);
    }
  }, [fail, handleSearch, orphanCandidates, storeCd, toast]);

  const handleDelete = useCallback(
    async (taxNo: number) => {
      if (!window.confirm('이 세금계산서를 생성취소합니다.')) {
        return;
      }
      try {
        await deleteTaxInvoice(taxNo);
        toast({ title: '생성 취소 완료' });
        await handleSearch();
      } catch (error) {
        fail('생성 취소 실패', error);
      }
    },
    [fail, handleSearch, toast]
  );

  const handlePreview = useCallback(
    async (taxNo: number) => {
      try {
        setPreviewDoc(buildTaxInvoiceDocument(await fetchTaxInvoiceDocument(taxNo)));
      } catch (error) {
        fail('미리보기 실패', error);
      }
    },
    [fail]
  );

  const statusOf = (row: IssuedTaxInvoiceRow) =>
    verifyResults.get(row.taxNo as number)?.status ?? row.verifyStatus ?? 'unmatched';
  const countBy = (status: string) => rows.filter(row => statusOf(row) === status).length;

  // 발행내역 그리드 행 - verify 응답이 있으면 그 판정·승인번호·전송일자가 우선이다(mismatch·canceled는 저장되지 않는다)
  const issuedGridRows = useMemo(
    () =>
      rows.map(row => {
        const verified = verifyResults.get(row.taxNo as number);
        const status = statusOf(row);
        return {
          taxNo: row.taxNo,
          issueDate: row.issueDate,
          // 미등록문서는 거래처가 해석되지 않아 거래처명이 없을 수 있다
          dealer: `${row.dealCode ?? '-'} ${row.dealName ?? ''}`.trim(),
          supplyAmtTotal: row.supplyAmtTotal,
          vatAmtTotal: row.vatAmtTotal,
          totalAmt: row.totalAmt,
          // 생성횟수가 1 이상이면 취소 불가. 미등록문서는 등록 즉시 1이라 항상 비활성이다
          btnCancel: row.cancelable ? '생성취소' : '',
          uploadGeCount: row.hometaxUploadGeCount,
          hometaxTaxNo: verified?.hometaxTaxNo ?? row.hometaxTaxNo ?? '',
          status: VERIFY_STATUS_LABEL[status] ?? status,
          uploadDt: verified?.hometaxUploadDt ?? row.hometaxUploadDt ?? '',
          // 사내 승인번호(문서번호) - 버튼으로 두어 누르면 계산서 미리보기
          btnDocNo: row.docNo ?? '',
        };
      }),
    [rows, verifyResults]
  );

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('tax-invoice')} />
      }
      pageHeader={<PageHeader onSearch={handleSearch} isLoading={isLoading} />}
      title='세금계산서 업로드 생성·검증'
      onExpandChange={handleExpandChange}
    >
      <div className='space-y-4'>
        <div className='flex flex-wrap items-end gap-3'>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>발행기간</label>
            <DateRangeInputs
              start={startDate}
              end={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </div>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>거래처</label>
            <Select
              value={dealCode || ALL}
              onValueChange={value => setDealCode(value === ALL ? '' : value)}
            >
              <SelectTrigger className='w-56'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>전체</SelectItem>
                {custList.map(d => (
                  <SelectItem key={d.dealCode} value={d.dealCode ?? ''}>
                    {d.dealCode} {d.dealName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {storeNotice && (
          <p className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
            {storeNotice}
          </p>
        )}

        {/* 정본 split-section — 좌: 세금계산서 발행내역, 우: 국세청 자료 */}
        <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
          <div className='space-y-1'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='shrink-0 whitespace-nowrap text-sm font-medium'>세금계산서 발행내역</div>
              <div className='flex flex-wrap justify-end gap-1'>
                <Button
                  size='sm'
                  onClick={handleGenerateUpload}
                  disabled={isLoading || selected.length === 0}
                >
                  업로드자료 생성
                </Button>
                {/* 국세청 목록이 로드되지 않은 상태에서는 체크와 무관하게 비활성(PRD §7) */}
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => void handleVerify()}
                  disabled={isLoading || selected.length === 0 || !ntsFile}
                >
                  검증(매칭)
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={handleCreateOrphans}
                  disabled={isLoading || orphanCandidates.length === 0}
                >
                  미등록문서 등록 ({orphanCandidates.length})
                </Button>
              </div>
            </div>
            <div className={`border rounded-lg ${paneHeight(isExpanded)}`}>
              <RealGridReact
                gridProps={{ ...GRID_BASE, checkBar: { visible: true } }}
                rows={issuedGridRows}
                onItemChecked={grid => setSelected(checkedRows(grid).map(r => r.taxNo as number))}
                onItemAllChecked={grid => setSelected(checkedRows(grid).map(r => r.taxNo as number))}
                // renderer:{type:'button'} 클릭은 onCellButtonClicked(button:'action' 전용)가 아니라 onCellItemClicked로 온다
                onCellItemClicked={(grid, index) => {
                  const taxNo = grid.getValue(index.itemIndex ?? -1, 'taxNo') as number;
                  if (index.fieldName === 'btnCancel') void handleDelete(taxNo);
                  else if (index.fieldName === 'btnDocNo') void handlePreview(taxNo);
                  return true;
                }}
              >
                <RGDataField fieldName='taxNo' dataType={RealGrid.ValueType.NUMBER} />
                <RGDataField fieldName='issueDate' dataType={RealGrid.ValueType.TEXT} />
                <RGDataField fieldName='dealer' dataType={RealGrid.ValueType.TEXT} />
                <RGDataField fieldName='supplyAmtTotal' dataType={RealGrid.ValueType.NUMBER} />
                <RGDataField fieldName='vatAmtTotal' dataType={RealGrid.ValueType.NUMBER} />
                <RGDataField fieldName='totalAmt' dataType={RealGrid.ValueType.NUMBER} />
                <RGDataField fieldName='btnCancel' dataType={RealGrid.ValueType.TEXT} />
                <RGDataField fieldName='uploadGeCount' dataType={RealGrid.ValueType.NUMBER} />
                <RGDataField fieldName='hometaxTaxNo' dataType={RealGrid.ValueType.TEXT} />
                <RGDataField fieldName='status' dataType={RealGrid.ValueType.TEXT} />
                <RGDataField fieldName='uploadDt' dataType={RealGrid.ValueType.TEXT} />
                <RGDataField fieldName='btnDocNo' dataType={RealGrid.ValueType.TEXT} />
                <RGDataColumn fieldName='issueDate' header={{ text: '작성일자' }} width={95} />
                <RGDataColumn fieldName='dealer' header={{ text: '거래처' }} width={160} />
                <RGDataColumn fieldName='supplyAmtTotal' header={{ text: '공급가액' }} width={105} numberFormat='#,##0' styleName='text-right' />
                <RGDataColumn fieldName='vatAmtTotal' header={{ text: '세액' }} width={95} numberFormat='#,##0' styleName='text-right' />
                <RGDataColumn fieldName='totalAmt' header={{ text: '합계금액' }} width={105} numberFormat='#,##0' styleName='text-right' />
                <RGDataColumn fieldName='btnCancel' header={{ text: '세금계산서' }} width={85} renderer={{ type: 'button', hideWhenEmpty: true }} />
                <RGDataColumn fieldName='uploadGeCount' header={{ text: '홈택스생성' }} width={80} numberFormat='#,##0' styleName='text-right' />
                <RGDataColumn fieldName='hometaxTaxNo' header={{ text: '홈택스승인번호' }} width={200} />
                <RGDataColumn fieldName='status' header={{ text: '검증상태' }} width={90} />
                <RGDataColumn fieldName='uploadDt' header={{ text: '전송일자' }} width={100} />
                <RGDataColumn fieldName='btnDocNo' header={{ text: '승인번호(사내)' }} width={170} renderer={{ type: 'button', hideWhenEmpty: true }} />
              </RealGridReact>
            </div>
          </div>

          <SourceDataPane
            tab={dataTab}
            onPickUpload={() => void pickFile().then(file => file && handleUploadFilePicked(file))}
            onPickNts={() => void pickFile().then(file => file && handleNtsFilePicked(file))}
            uploadPreview={uploadPreview}
            ntsFile={ntsFile}
            isExpanded={isExpanded}
          />
        </div>

        {/* 정본 status-bar */}
        <div className='flex gap-6 text-xs text-muted-foreground'>
          <span>
            발행내역 전체 <b>{rows.length}</b>건
          </span>
          <span>
            매칭완료(일치) <b>{countBy('matched')}</b>건
          </span>
          <span>
            금액불일치 <b>{countBy('mismatch')}</b>건
          </span>
          <span>
            홈택스 취소확인 <b>{countBy('canceled')}</b>건
          </span>
          <span>
            미매칭 <b>{countBy('unmatched')}</b>건
          </span>
        </div>
      </div>

      {previewDoc && (
        <PrintPreviewOverlay
          title={`${previewDoc.titleText} ${previewDoc.approvalNo}`}
          onClose={() => setPreviewDoc(null)}
        >
          <TaxInvoiceDocument doc={previewDoc} />
        </PrintPreviewOverlay>
      )}
    </ExpandableCard>
  );
}

function paneHeight(isExpanded: boolean) {
  return isExpanded ? 'h-[calc(100vh-400px)]' : 'h-[calc(100vh-460px)]';
}

/**
 * 우측 "국세청 자료" 패널(정본). 헤더에 홈택스 발급 접속 링크와 탭 버튼 2개 - 탭을 누르면 바로 파일
 * 선택 대화상자가 뜨고, 불러온 파일의 내용을 원본 컬럼 그대로 표에 그린다(별도 "불러오기" 버튼 없음).
 */
function SourceDataPane({
  tab,
  onPickUpload,
  onPickNts,
  uploadPreview,
  ntsFile,
  isExpanded,
}: {
  tab: 'upload' | 'nts';
  onPickUpload: () => void;
  onPickNts: () => void;
  uploadPreview: { columns: string[]; rows: unknown[][] } | null;
  ntsFile: NtsListParseResult | null;
  isExpanded: boolean;
}) {
  const columns = (tab === 'upload' ? uploadPreview?.columns : ntsFile?.headers) ?? [];
  const body = (tab === 'upload' ? uploadPreview?.rows : ntsFile?.rawRows) ?? [];
  // 원본 컬럼이 파일마다 달라 c0..cN 필드로 싣는다. 금액이 음수(마이너스 발행분, 수정세금계산서 취소분)인
  // 국세청 목록 행은 빨간 글씨(PRD §5)
  const gridRows = useMemo(
    () =>
      body.map((row, index) => ({
        ...Object.fromEntries(columns.map((_, c) => [`c${c}`, String((row as unknown[])[c] ?? '')])),
        _neg: tab === 'nts' && (ntsFile?.rows[index]?.total ?? 0) < 0,
      })),
    [body, columns, ntsFile, tab]
  );

  return (
    <div className='space-y-1'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div className='shrink-0 whitespace-nowrap text-sm font-medium'>국세청 자료</div>
        <div className='flex flex-wrap justify-end gap-1'>
          <Button
            size='sm'
            variant='outline'
            className='px-2 min-[1660px]:px-3'
            aria-label='국세청 세금계산서 발급 접속'
            title='국세청 세금계산서 발급 접속'
            onClick={() => window.open(HOMETAX_ISSUE_URL, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink />
            <span className='hidden min-[1660px]:inline'>국세청 세금계산서 발급 접속</span>
          </Button>
          <Button
            size='sm'
            variant={tab === 'upload' ? 'default' : 'outline'}
            title='업로드 생성자료(세금계산서등록양식)'
            onClick={onPickUpload}
          >
            <span className='min-[1660px]:hidden'>업로드 생성자료</span>
            <span className='hidden min-[1660px]:inline'>업로드 생성자료(세금계산서등록양식)</span>
          </Button>
          <Button
            size='sm'
            variant={tab === 'nts' ? 'default' : 'outline'}
            title='매출전자세금계산서목록'
            onClick={onPickNts}
          >
            <span className='min-[1660px]:hidden'>국세청 목록</span>
            <span className='hidden min-[1660px]:inline'>매출전자세금계산서목록</span>
          </Button>
        </div>
      </div>
      <div className={`border rounded-lg ${paneHeight(isExpanded)}`}>
        {columns.length === 0 ? (
          <p className='p-4 text-sm text-muted-foreground'>
            좌측에서 발행내역을 선택하고 [업로드자료 생성]을 실행하거나, 위 탭을 클릭해 국세청 홈택스에서
            내려받은 파일을 불러오면 여기에 표시됩니다.
          </p>
        ) : (
          <RealGridReact
            key={`${tab}-${columns.length}`}
            gridProps={{ ...GRID_BASE, checkBar: { visible: false } }}
            rows={gridRows}
            rowStyleCallback={(grid, item) =>
              grid.getDataSource().getValue(item.dataRow, '_neg') ? { style: { color: '#dc2626' } } : undefined
            }
          >
            <RGDataField fieldName='_neg' dataType={RealGrid.ValueType.BOOLEAN} />
            {columns.map((_, c) => (
              <RGDataField key={`f${c}`} fieldName={`c${c}`} dataType={RealGrid.ValueType.TEXT} />
            ))}
            {columns.map((column, c) => (
              <RGDataColumn key={`col${c}`} fieldName={`c${c}`} header={{ text: column }} width={110} />
            ))}
          </RealGridReact>
        )}
      </div>
    </div>
  );
}
